import { DraftHandover, applyDraftToForm } from '@/modules/assistant';
import type { AssistantDraft } from '@/api/schemas/assistant';

/**
 * The tenant form's values, as the assistant's hand-over sees them.
 */
export type ProvisionFormValues = {
  slug: string;
  displayName: string;
  legalName: string;
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  ownerAccountId: string;
};

/**
 * The fields this form has.
 *
 * The draft for `tenant.create` also names `country`, which this form does not have: the
 * deployment's country is fixed, not chosen per tenant. The hand-over says so rather than
 * dropping the proposal silently.
 */
const PROVISION_DRAFT_FIELDS: readonly string[] = [
  'slug',
  'displayName',
  'legalName',
  'contactName',
  'contactEmail',
  'contactPhone',
  'ownerAccountId',
];

export type TenantDraftBridgeProps = {
  /** The form's current values. */
  readonly values: ProvisionFormValues;
  /** Called with the values to write into the form. */
  readonly onApply: (values: ProvisionFormValues) => void;
};

/**
 * THE HAND-OVER, AS THE TENANT FORM SEES IT.
 *
 * ---------------------------------------------------------------------------
 * WHY A BRIDGE AND NOT THE NOTICE ITSELF
 * ---------------------------------------------------------------------------
 * The notice lives in the assistant module, and the assistant module knows nothing about
 * this form's state. This bridge is the small piece that knows both: it passes the form's
 * OWN field names to the notice, and it writes the applied values back into the form's
 * OWN shape.
 *
 * So the rule that a draft may only fill fields the form HAS is enforced in one place, and
 * it is enforced by the form's own field list — not by a cast, and not by a convention
 * somebody has to remember.
 *
 * ---------------------------------------------------------------------------
 * IT WRITES NOTHING
 * ---------------------------------------------------------------------------
 * The bridge fills the form's state. The ordinary form then performs the ordinary write,
 * with the ordinary validation, when the person submits it. The assistant proposes; the
 * person confirms; the form does the rest.
 */
export function TenantDraftBridge({ values, onApply }: TenantDraftBridgeProps) {
  return (
    <DraftHandover
      capability="tenant.create"
      fieldNames={PROVISION_DRAFT_FIELDS}
      onApply={(draft: AssistantDraft) => {
        const applied = applyDraftToForm(draft, values);
        onApply({
          slug: applied.values['slug'] ?? values.slug,
          displayName: applied.values['displayName'] ?? values.displayName,
          legalName: applied.values['legalName'] ?? values.legalName,
          contactName: applied.values['contactName'] ?? values.contactName,
          contactEmail: applied.values['contactEmail'] ?? values.contactEmail,
          contactPhone: applied.values['contactPhone'] ?? values.contactPhone,
          ownerAccountId: applied.values['ownerAccountId'] ?? values.ownerAccountId,
        });
      }}
    />
  );
}
